import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  Icon,
  Text,
} from '@gousto-internal/citrus-react'

import type { MenuRecipeGeneralTag, StandardRecipeItem } from '@library/api-menu-experience'

import { LabelPill } from '../RecipeTile.styles'
import { getRecipeTileGeneralTags } from '../RecipeTile.utils'

type GeneralLabelRowProps = {
  recipe: StandardRecipeItem
  isOutOfStock: boolean
  reserveCtaSpace: boolean
}

export const GeneralLabelRow = ({
  recipe,
  isOutOfStock,
  reserveCtaSpace,
}: GeneralLabelRowProps) => {
  const labels = getRecipeTileGeneralTags(recipe)

  if (!isOutOfStock && labels.length === 0) {
    return null
  }

  const wrapperStyle: React.CSSProperties = {
    position: DisplayPosition.Absolute,
    top: '1rem',
    left: '1rem',
    transform: 'translateY(calc(-50% - 1rem))',
    zIndex: 12,
    minWidth: 0,
    maxWidth: reserveCtaSpace ? 'calc(100% - 88px)' : 'calc(100% - 2rem)',
    overflow: 'hidden',
  }

  if (isOutOfStock) {
    return (
      <Box style={wrapperStyle}>
        <LabelPill
          data-testid="sold-out-tag"
          style={{
            backgroundColor: '#4F5C68',
            color: '#FFFFFF',
          }}
        >
          <Text size={2} style={{ color: 'inherit', lineHeight: '24px' }}>
            Sold Out
          </Text>
        </LabelPill>
      </Box>
    )
  }

  return (
    <Box
      data-testid="recipe-tile-general-labels"
      display={Display.Flex}
      gap={1}
      style={wrapperStyle}
    >
      {labels.map((label) => (
        <GeneralLabel key={`${label.type}-${label.text}`} label={label} />
      ))}
    </Box>
  )
}

const GeneralLabel = ({ label }: { label: MenuRecipeGeneralTag }) => {
  if (label.type === 'invalidPreference') {
    return (
      <Box
        data-testid="recipe-tile-invalid-preference-label"
        borderRadius={1.5}
        height="24px"
        display={Display.Flex}
        alignItems={AlignItems.Center}
        gap={1}
        style={{
          backgroundColor: label.backgroundColor.light,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Box
          data-testid="recipe-tile-invalid-preference-icon"
          style={{ borderTopLeftRadius: 1.5, borderBottomLeftRadius: 1.5 }}
          bg={Color.Warning_700}
          width="22px"
          paddingH={0.75}
          paddingV={1}
          display={Display.Flex}
        >
          <Icon name="info" style={{ color: Color.White }} size={4} />
        </Box>
        <Box paddingRight={2} style={{ backgroundColor: label.backgroundColor.light, minWidth: 0 }}>
          <Text
            size={2}
            style={{
              color: label.color.light,
              lineHeight: '24px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {label.text}
          </Text>
        </Box>
      </Box>
    )
  }

  return (
    <LabelPill
      style={{
        backgroundColor: label.backgroundColor.light,
        color: label.color.light,
      }}
    >
      <Text
        size={2}
        style={{
          color: 'inherit',
          lineHeight: '24px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label.text}
      </Text>
    </LabelPill>
  )
}
